import he from 'he';
import fp from 'lodash/fp';
import {
  cpdPointStatus,
  exportCpdActivityMap,
  getCpdActivityTitle,
  getCpdActivityTransform,
  kaplanMapping,
  mapCpdPoint,
  kaplanMapping2022,
  kaplanCpdType,
  kaplanAccreditationDetails2022
} from '../constants';
import { filterNonLegacy } from '../common/cpd-category-filter';

const excludeCpdCategory = fp.filter((key) => key !== 'cpdCategory');

const sortCpdCategorys = (cpdCategorys) => {
  const sorted = fp.sortBy([
    ({ legacy }) => legacy,
    ({ parentCpdCategoryId }) => !!parentCpdCategoryId,
    'name'
  ])(cpdCategorys);
  return sorted;
};

export const getCpdActivityColumns = (
  industryCpdCategorys,
  userIndustryGoals
) => {
  const cpdCategoryColumns = fp.flow(
    filterNonLegacy,
    sortCpdCategorys,
    fp.map(({ name }) => [name, he.decode(name)]),
    fp.fromPairs
  )(industryCpdCategorys);

  const cpdColumns = fp.flow(
    fp.keys,
    excludeCpdCategory,
    fp.map((key) => [key, getCpdActivityTitle(key)(exportCpdActivityMap)]),
    fp.fromPairs
  )(exportCpdActivityMap);

  const industryGoalColumns = fp.flow(
    fp.map(({ name }) => [name, name]),
    fp.fromPairs
  )(userIndustryGoals);

  const documentedColumns2022 = fp.flow(
    fp.keys,
    // Filter out the imported kaplan categories since it will be recorded in Caddie categories.
    fp.filter((key) => kaplanMapping2022[key].type === kaplanCpdType.document),
    fp.map((key) => [`${key}-kaplan-documented-2022`, key]),
    fp.fromPairs
  )(kaplanMapping2022);

  const kaplanAccreditationDetailsColumns2022 = fp.flow(
    fp.map((accreditationName) => [
      accreditationName,
      `${accreditationName} Hours`
    ]),
    fp.flatten,
    fp.map((key) => [key, key]),
    fp.fromPairs
  )(kaplanAccreditationDetails2022);

  const documentedColumns = fp.flow(
    fp.keys,
    // Filter out the imported kaplan categories since it will be recorded in Caddie categories.
    fp.filter((key) => kaplanMapping[key].type === kaplanCpdType.document),
    fp.map((key) => [`${key}-kaplan-documented`, `${key} pre-2019`]),
    fp.fromPairs
  )(kaplanMapping);

  const columns = {
    ...cpdColumns,
    ...industryGoalColumns,
    ...cpdCategoryColumns,
    ...documentedColumns2022,
    ...kaplanAccreditationDetailsColumns2022,
    ...documentedColumns
  };
  return columns;
};

export const getUserCpdActivityColumns = (
  industryCpdCategorys,
  userIndustryGoals
) => {
  const userColumns = {
    User: 'User'
  };
  const cpdActivityColumns = getCpdActivityColumns(
    industryCpdCategorys,
    userIndustryGoals
  );
  const columns = {
    ...userColumns,
    ...cpdActivityColumns
  };
  return columns;
};

// RM CPD Goal name. Used to handle the RM CPD progress calculation only.
// Note that this name is hard coded in Database via migration file.
const RMCpdName = 'RM CPD';

// Name of the RM CPD category, since the RM CPD Goal needs to be managed separately where
// Note that this name is hard coded in Database via migration file.
// This is also configurable from the Admin Portal, this needs to be updated if it is updated from the UI.
const RMCategoryName = 'Responsible Managers CPD';
const RMLegacyCategoryName = 'Responsible Managers CPD (LEGACY)';

const isRMCategory = ({ name }) =>
  name === RMCategoryName || name === RMLegacyCategoryName;

const getCpdGoalTransformedPoints = (
  goalName,
  cpdPoint,
  cpdCategorys,
  cpdPointsTransformFunc
) => {
  if (goalName === RMCpdName) {
    const rmCategoryIds = fp.flow(
      fp.filter(isRMCategory),
      fp.map(({ id }) => id)
    )(cpdCategorys);

    // If an RM subtopic is allocated, only those hours count. Otherwise the content is
    // assigned the RM goal, so all of its core hours count. Subtopic-only content (no core
    // allocation) falls back to its subtopic hours.
    const rmAllocations = fp.filter(({ cpdCategoryId }) =>
      fp.includes(cpdCategoryId)(rmCategoryIds)
    )(cpdPoint.cpdPointAllocation);

    const nonRmAllocations = fp.filter(
      ({ cpdCategoryId }) => !fp.includes(cpdCategoryId)(rmCategoryIds)
    )(cpdPoint.cpdPointAllocation);

    const cpdPoints =
      rmAllocations.length > 0
        ? fp.sumBy('points')(rmAllocations)
        : cpdPoint.totalPoints > 0
        ? cpdPoint.totalPoints
        : fp.sumBy('points')(nonRmAllocations);

    return cpdPointsTransformFunc({
      status: cpdPoint.status,
      cpdPoints
    });
  }

  return cpdPointsTransformFunc({
    status: cpdPoint.status,
    cpdPoints: cpdPoint.totalPoints
  });
};

export const mapCpdActivity = (cpdCategorys, publishers, userIndustryGoals) =>
  fp.flow(
    fp.filter((cpdPoint) => cpdPoint.status !== cpdPointStatus.draft),
    fp.map((cpdPoint) => {
      const mappedCpdPoint = mapCpdPoint(cpdCategorys, publishers)(cpdPoint);
      const cpdValues = fp.flow(
        fp.keys,
        excludeCpdCategory,
        fp.map((key) =>
          getCpdActivityTransform(key)(exportCpdActivityMap)(mappedCpdPoint)
        )
      )(exportCpdActivityMap);

      const cpdPointsTransformFunc =
        getCpdActivityTransform('cpdPoints')(exportCpdActivityMap);
      const industryGoalValues = fp.map(({ id, name }) =>
        fp.includes(id)(cpdPoint.goals)
          ? getCpdGoalTransformedPoints(
              name,
              cpdPoint,
              cpdCategorys,
              cpdPointsTransformFunc
            )
          : '0'
      )(userIndustryGoals);

      const cpdCategoryValues = fp.flow(
        filterNonLegacy,
        sortCpdCategorys,
        fp.map(({ id }) => {
          const allocatedPoints = fp.filter(
            ({ cpdCategoryId }) => cpdCategoryId === id
          )(cpdPoint.cpdPointAllocation);
          return fp.sumBy('points')(allocatedPoints);
        })
      )(cpdCategorys);

      const documentedValues2022 = fp.flow(
        fp.keys,
        fp.filter(
          (key) => kaplanMapping2022[key].type === kaplanCpdType.document
        ),
        fp.map((kaplanName) => {
          const documentedPoints = fp.filter(({ name }) => name === kaplanName)(
            cpdPoint.documentedCpdPoints
          );
          return fp.sumBy('points')(documentedPoints);
        })
      )(kaplanMapping2022);

      const accreditationDetails2022 = fp.flow(
        fp.map((accreditationName) => {
          const accreditationDetails =
            fp.get('additionalData.accreditationDetails')(cpdPoint) ||
            fp.get('externalCpd.additionalData.accreditationDetails')(cpdPoint);

          if (accreditationDetails !== undefined) {
            const targetAccreditationDetails = fp.flow(
              fp.filter(({ name }) => name === accreditationName),
              fp.first
            )(accreditationDetails);

            if (targetAccreditationDetails !== undefined) {
              return [
                targetAccreditationDetails.number,
                targetAccreditationDetails.points
              ];
            }
          }

          return ['', ''];
        }),
        fp.flatten
      )(kaplanAccreditationDetails2022);

      const documentedValues = fp.flow(
        fp.keys,
        fp.filter((key) => kaplanMapping[key].type === kaplanCpdType.document),
        fp.map((kaplanName) => {
          const documentedPoints = fp.filter(({ name }) => name === kaplanName)(
            cpdPoint.documentedCpdPoints
          );
          return fp.sumBy('points')(documentedPoints);
        })
      )(kaplanMapping);

      const values = cpdValues
        .concat(industryGoalValues)
        .concat(cpdCategoryValues)
        .concat(documentedValues2022)
        .concat(accreditationDetails2022)
        .concat(documentedValues);

      return values;
    })
  );

export const mapUserCpdActivity = (
  cpdCategorys,
  publishers,
  userIndustryGoals
) =>
  fp.flow(
    fp.filter(({ cpdPoint }) => cpdPoint.status !== cpdPointStatus.draft),
    fp.map(({ user, cpdPoint }) => {
      const mappedCpdPoint = mapCpdPoint(cpdCategorys, publishers)(cpdPoint);
      const cpdValues = fp.flow(
        fp.keys,
        excludeCpdCategory,
        fp.map((key) =>
          getCpdActivityTransform(key)(exportCpdActivityMap)(mappedCpdPoint)
        )
      )(exportCpdActivityMap);

      const cpdPointsTransformFunc =
        getCpdActivityTransform('cpdPoints')(exportCpdActivityMap);
      const industryGoalValues = fp.map(({ id, name }) =>
        fp.includes(id)(cpdPoint.goals)
          ? getCpdGoalTransformedPoints(
              name,
              cpdPoint,
              cpdCategorys,
              cpdPointsTransformFunc
            )
          : '0'
      )(userIndustryGoals);

      const cpdCategoryValues = fp.flow(
        filterNonLegacy,
        sortCpdCategorys,
        fp.map(({ id }) => {
          const allocatedPoints = fp.filter(
            ({ cpdCategoryId }) => cpdCategoryId === id
          )(cpdPoint.cpdPointAllocation);
          return fp.sumBy('points')(allocatedPoints);
        })
      )(cpdCategorys);

      const documentedValues2022 = fp.flow(
        fp.keys,
        fp.filter(
          (key) => kaplanMapping2022[key].type === kaplanCpdType.document
        ),
        fp.map((kaplanName) => {
          const documentedPoints = fp.filter(({ name }) => name === kaplanName)(
            cpdPoint.documentedCpdPoints
          );
          return fp.sumBy('points')(documentedPoints);
        })
      )(kaplanMapping2022);

      const accreditationDetails2022 = fp.flow(
        fp.map((accreditationName) => {
          const accreditationDetails =
            fp.get('additionalData.accreditationDetails')(cpdPoint) ||
            fp.get('externalCpd.additionalData.accreditationDetails')(cpdPoint);

          if (accreditationDetails !== undefined) {
            const targetAccreditationDetails = fp.flow(
              fp.filter(({ name }) => name === accreditationName),
              fp.first
            )(accreditationDetails);

            if (targetAccreditationDetails !== undefined) {
              return [
                targetAccreditationDetails.number,
                targetAccreditationDetails.points
              ];
            }
          }

          return ['', ''];
        }),
        fp.flatten
      )(kaplanAccreditationDetails2022);

      const documentedValues = fp.flow(
        fp.keys,
        fp.filter((key) => kaplanMapping[key].type === kaplanCpdType.document),
        fp.map((kaplanName) => {
          const documentedPoints = fp.filter(({ name }) => name === kaplanName)(
            cpdPoint.documentedCpdPoints
          );
          return fp.sumBy('points')(documentedPoints);
        })
      )(kaplanMapping);

      const values = [user.email]
        .concat(cpdValues)
        .concat(industryGoalValues)
        .concat(cpdCategoryValues)
        .concat(documentedValues2022)
        .concat(accreditationDetails2022)
        .concat(documentedValues);

      return values;
    })
  );



// WEBPACK FOOTER //
// ./src/services/cpd-activity.js